/**
 * This class provides the `uploadFile` method to upload file to S3 with an
 * authorization provided by a request to an authorization URL. The fetch
 * options is a callable with the interface of `fetch` used to run the request
 * on the authorization backend.
 *
 * The request to the S3 host is done with the global fetch instance.
 */

import invariant from 'invariant';

export default class UploadRequest {
    constructor(authorizationURL, options = {}) {
        this.authorizationURL = authorizationURL;
        this.fetch = options.fetch || global.fetch.bind(global);
    }

    async _fetchUploadConfig() {
        // Request an Upload authorization
        // https://boto3.amazonaws.com/v1/documentation/api/latest/reference/services/s3.html
        const response = await this.fetch(this.authorizationURL, {
            method: 'POST'
        });

        invariant(response, 'fetch callback returned an empty value');
        invariant(typeof response.ok === 'boolean', 'fetch callback returned an object that is not a response');
        if (!response.ok) {
            throw new Error(`request on ${this.authorizationURL} failed with ${response.status}`);
        }
        return response.json();
    }

    async uploadFile(file) {
        const { url, fields } = await this._fetchUploadConfig();
        invariant(url, 'url should have been returned by the fetch call');
        invariant(fields, 'fields should have been returned by the fetch call');

        const body = new FormData();
        Object.keys(fields).forEach(key => body.append(key, fields[key]));
        body.append('file', file);

        //Empty header to prevent authentication by fetch
        const headers = { Authorization: '' };

        await this.fetch(url, {
            method: 'POST',
            body,
            headers: headers,
            // Stop fetcher from sending authorization header
            auth: false
        });

        const { key } = fields;
        return key;
    }
}
